import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, LoaderCircle, FileText, Film } from 'lucide-react';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';

// The two artifacts a video visit leaves behind — its transcript and its
// recording — viewable from anywhere a visit is listed (the Video Visits
// tab, the client folder). Both are staff-only server-side.

const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

function Overlay({ title, Icon, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 bg-overlay backdrop-blur-lg flex items-center justify-center z-[300] p-4 animate-fade-in" onClick={onClose}>
      <div
        className={`w-full ${wide ? 'max-w-3xl' : 'max-w-xl'} max-h-[85vh] flex flex-col bg-elevated border border-theme rounded-[14px] shadow-xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-theme">
          <span className="text-[13px] font-semibold text-primary-c flex items-center gap-2">
            <Icon size={15} className="text-accent-light" /> {title}
          </span>
          <button onClick={onClose} className="text-muted-c hover:text-primary-c transition-colors"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function TranscriptModal({ session, onClose }) {
  const { currentOrg } = useOrg();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getTelehealthUtterances(currentOrg.id, session.id)
      .then(setRows)
      .catch((err) => setError(err.message));
  }, [currentOrg.id, session.id]);

  const download = useCallback(() => {
    const text = (rows || [])
      .map((r) => `[${fmtTime(r.spoken_at)}] ${r.speaker_name || 'Speaker'}${r.language_code ? ` (${r.language_code})` : ''}: ${r.content}`)
      .join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `visit-transcript-${session.id.slice(0, 8)}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [rows, session.id]);

  return (
    <Overlay title="Visit transcript" Icon={FileText} onClose={onClose}>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {error && <p className="text-[12px] text-red-400">{error}</p>}
        {!rows && !error && (
          <p className="text-[12px] text-muted-c flex items-center gap-2"><LoaderCircle size={14} className="animate-spin" /> Loading transcript…</p>
        )}
        {rows?.length === 0 && (
          <p className="text-[12px] text-muted-c">No transcript was captured for this visit.</p>
        )}
        {rows?.map((r) => (
          <div key={r.result_id}>
            <div className="flex items-baseline gap-2">
              <span className="text-[11px] font-semibold text-accent-light">{r.speaker_name || 'Speaker'}</span>
              <span className="text-[10px] text-muted-c">{fmtTime(r.spoken_at)}{r.language_code ? ` · ${r.language_code}` : ''}</span>
            </div>
            <p className="text-[12.5px] text-secondary-c leading-relaxed">{r.content}</p>
          </div>
        ))}
      </div>
      {rows?.length > 0 && (
        <div className="px-5 py-3 border-t border-theme">
          <button
            onClick={download}
            className="px-4 py-[8px] rounded-lg text-[12px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all flex items-center gap-1.5"
          >
            <Download size={13} /> Download transcript
          </button>
        </div>
      )}
    </Overlay>
  );
}

export function RecordingModal({ session, onClose }) {
  const { currentOrg } = useOrg();
  const [url, setUrl] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.getTelehealthRecordingUrl(currentOrg.id, session.id)
      .then((res) => {
        if (res.url) setUrl(res.url);
        else setNotice(res.message || 'The recording is not ready yet.');
      })
      .catch((err) => setError(err.message));
  }, [currentOrg.id, session.id]);

  return (
    <Overlay title="Visit recording" Icon={Film} onClose={onClose} wide>
      <div className="p-5">
        {error && <p className="text-[12px] text-red-400">{error}</p>}
        {notice && <p className="text-[12.5px] text-muted-c py-8 text-center">{notice}</p>}
        {!url && !notice && !error && (
          <p className="text-[12px] text-muted-c flex items-center gap-2 py-8 justify-center"><LoaderCircle size={14} className="animate-spin" /> Fetching recording…</p>
        )}
        {url && (
          <video src={url} controls autoPlay className="w-full rounded-[10px] bg-black max-h-[60vh]" />
        )}
      </div>
    </Overlay>
  );
}
